import {
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'

export type TaxMode =
  | { mode: 'percent'; amount: number } // amount = entered % (e.g. 10 means 10%)
  | { mode: 'dollar'; amount: number } // amount = entered $
  | { mode: 'custom'; percentage: string } // percentage from Tax.percentage (e.g. "0.05")

interface ComputeExpenseLineItemTotalsParams {
  expenseType: ExpenseTypeEnum
  subtotal: number
  tax: TaxMode
  tips: number
  mileageRate: number
  distance: number
  hourlyRate: number
  hoursOfWork: number
}

interface ComputeExpenseLineItemTotalsResult {
  computedSubtotal: number
  computedTaxAmount: number
  computedTotal: number
}

interface ComputeSubtotalFromTotalParams {
  total: number
  tax: TaxMode
  expenseType: ExpenseTypeEnum
  /** Ignored for non-MEALS expense types; `executeReverse` only subtracts tips when expenseType is MEALS. */
  tips: number
}

interface ComputeSubtotalFromTotalResult {
  computedSubtotal: number
}

// Rounds to two decimals (cents precision). Nudges in cents-space so the
// epsilon survives at currency magnitudes (`Number.EPSILON` alone is only
// meaningful near magnitude 1, so e.g. round2(10.005) would otherwise
// collapse to 10 instead of 10.01). Rounding is done in absolute space
// with the sign reapplied so halves round symmetrically away from zero
// for negatives — Math.round's default half-to-+∞ behaviour would
// otherwise give e.g. -1.015 → -1.01 while +1.015 → +1.02.
export const round2 = (value: number): number => {
  if (value === 0) return 0
  const sign = value < 0 ? -1 : 1
  return (sign * Math.round(Math.abs(value) * 100 + Number.EPSILON * 100)) / 100
}

export const parsePercentageOrZero = (raw: string): number => {
  const n = parseFloat(raw)
  return Number.isFinite(n) ? n : 0
}

const computeSubtotal = (
  values: ComputeExpenseLineItemTotalsParams
): number => {
  switch (values.expenseType) {
    case ExpenseTypes.MILEAGE:
      return round2(values.mileageRate * values.distance)
    case ExpenseTypes.LABOUR:
      return round2(values.hourlyRate * values.hoursOfWork)
    default:
      return values.subtotal
  }
}

const computeTaxAmount = (subtotal: number, tax: TaxMode): number => {
  switch (tax.mode) {
    case 'percent':
      return round2(subtotal * (tax.amount / 100))
    case 'dollar':
      return tax.amount
    case 'custom':
      return round2(subtotal * parsePercentageOrZero(tax.percentage))
  }
}

const computeTotal = (
  subtotal: number,
  taxAmount: number,
  tips: number,
  expenseType: ExpenseTypeEnum
): number => {
  const base = subtotal + taxAmount
  if (expenseType === ExpenseTypes.MEALS) {
    return round2(base + tips)
  }
  return round2(base)
}

export class ComputeExpenseLineItemTotalsUseCase {
  execute = (
    values: ComputeExpenseLineItemTotalsParams
  ): ComputeExpenseLineItemTotalsResult => {
    const computedSubtotal = computeSubtotal(values)
    const computedTaxAmount = computeTaxAmount(computedSubtotal, values.tax)
    const computedTotal = computeTotal(
      computedSubtotal,
      computedTaxAmount,
      values.tips,
      values.expenseType
    )

    return {
      computedSubtotal,
      computedTaxAmount,
      computedTotal,
    }
  }

  executeReverse = (
    values: ComputeSubtotalFromTotalParams
  ): ComputeSubtotalFromTotalResult => {
    const effectiveTips =
      values.expenseType === ExpenseTypes.MEALS ? values.tips : 0
    const netTotal = values.total - effectiveTips

    const divideByRate = (rate: number): number => {
      const denominator = 1 + rate
      return denominator <= 0 ? 0 : round2(netTotal / denominator)
    }

    let computedSubtotal: number

    switch (values.tax.mode) {
      case 'dollar':
        computedSubtotal = round2(netTotal - values.tax.amount)
        break
      case 'percent':
        computedSubtotal = divideByRate(values.tax.amount / 100)
        break
      case 'custom':
        computedSubtotal = divideByRate(
          parsePercentageOrZero(values.tax.percentage)
        )
        break
      default: {
        const _exhaustive: never = values.tax
        throw new Error(`Unhandled tax mode: ${JSON.stringify(_exhaustive)}`)
      }
    }

    return { computedSubtotal }
  }
}
